import React, { Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Boxes,
  ShieldCheck,
  Zap,
  Users,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Eyebrow } from "../components/Reveal";
import { PrimaryButton, SecondaryButton } from "../components/Buttons";
import { MODULES } from "../data/modules";

const Hero3D = lazy(() => import("../components/Hero3D"));

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Velacin",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "Velacin unifies HR, project management, timesheets, expenses, recruitment and analytics into one modern workspace.",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "128",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Velacin",
    url: "https://velacin.com",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://velacin.com/features?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  },
];

const STATS = [
  { value: "20+", label: "Integrated modules" },
  { value: "80+", label: "Capability flags" },
  { value: "Unlimited", label: "Role types" },
  { value: "Self-host or cloud", label: "Deployment" },
];

const PILLARS = [
  {
    icon: Boxes,
    title: "One workspace",
    body: "HR, PM, timesheets, expenses, recruitment and analytics — finally in the same tool. No context switching, no duplicated data, no glue code.",
  },
  {
    icon: ShieldCheck,
    title: "Design your own permissions",
    body: "Custom roles with granular capability flags. Multi-role users get the union. Guardrails keep admin lockout impossible.",
  },
  {
    icon: Zap,
    title: "Built for teams that ship",
    body: "Live timers, drag-and-drop kanban, sprint velocity, burndown, gamification. Every module respects the pace of real work.",
  },
  {
    icon: Users,
    title: "People-first defaults",
    body: "Assignable HRs, targeted announcements, transparent idle tracking, and configurable leave policies — built around humans, not surveillance.",
  },
];

const HeroFallback = () => (
  <div
    className="absolute inset-0"
    aria-hidden="true"
    style={{
      background:
        "radial-gradient(60% 50% at 50% 40%, rgba(76,110,245,0.35) 0%, rgba(0,47,167,0.15) 45%, rgba(0,0,0,0) 75%)",
    }}
  />
);

export default function Home() {
  return (
    <>
      <Seo
        title="Velacin — Modern HR & Project Management for Teams That Ship"
        description="Velacin unifies HR, project management, timesheets, expenses, recruitment and analytics in one modern workspace. Attendance, leave, kanban, sprints, gamification — all in one place."
        path="/"
        keywords="HR software, project management, kanban, timesheet, attendance tracking, leave management, expense management, recruitment software, employee management, SaaS, workforce management, sprint planning, gamification, custom roles, RBAC"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative min-h-screen overflow-hidden">
        <div className="absolute inset-0 opacity-90">
          <Suspense fallback={<HeroFallback />}>
            <Hero3D />
          </Suspense>
        </div>
        {/* legibility overlay */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(to right, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0) 75%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.85) 100%)",
          }}
        />

        <div className="relative mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-6 pt-28 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-[#8FA4FF] backdrop-blur">
              The workspace teams that ship deserve
            </span>

            <h1 className="mt-6 text-5xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl">
              Run people <span className="font-light italic text-white/80">and</span>
              <br />
              projects.
              <br />
              <span className="text-gradient-blue">In one place.</span>
            </h1>

            <p className="mt-7 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
              Velacin is a modern, unified workspace that combines HR, project
              management, timesheets, expenses, recruitment, and analytics — so
              your team can stop stitching tools together and start shipping.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <PrimaryButton to="/contact" testId="hero-book-demo">
                Book a demo
              </PrimaryButton>
              <SecondaryButton to="/features" testId="hero-explore-modules">
                Explore modules
              </SecondaryButton>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/10 bg-[#0A0A0A]">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-6 py-14 md:grid-cols-4 lg:px-8">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div data-testid={`stat-${i}`}>
                <div className="text-3xl font-extrabold tracking-tight text-white md:text-4xl">
                  {s.value}
                </div>
                <div className="mt-2 font-mono-label text-xs uppercase tracking-widest text-white/50">
                  {s.label}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-black py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Why Velacin</Eyebrow>
            <h2 className="mt-4 max-w-3xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
              A workspace with the parts that usually go missing.
            </h2>
            <p className="mt-5 max-w-2xl text-base text-white/60 md:text-lg">
              Most tools solve one problem. Velacin solves the seven problems
              your team actually has — without pretending they're unrelated.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <article
                  data-testid={`pillar-${i}`}
                  className="card-surface h-full rounded-3xl p-8"
                >
                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#4C6EF5]/15 text-[#8FA4FF]">
                    <p.icon className="h-6 w-6" strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-6 text-xl font-semibold text-white">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">
                    {p.body}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MODULES SHOWCASE */}
      <section className="bg-[#0A0A0A] py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <Reveal>
              <Eyebrow>Capabilities</Eyebrow>
              <h2 className="mt-4 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
                Twenty modules. One coherent product.
              </h2>
              <p className="mt-5 max-w-2xl text-base text-white/60 md:text-lg">
                Every module is designed to work with every other module.
                Timesheets feed budgets. Attendance feeds insights. Achievements
                come from your actual work.
              </p>
            </Reveal>
            <Link
              to="/features"
              data-testid="showcase-view-all"
              className="group inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-[#8FA4FF] hover:text-white"
            >
              View all modules
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {MODULES.slice(0, 9).map((m, i) => (
              <Reveal key={m.slug} delay={(i % 3) * 0.06}>
                <Link
                  to={`/features#${m.slug}`}
                  data-testid={`showcase-card-${m.slug}`}
                  className="card-surface group flex h-full flex-col rounded-3xl p-7"
                >
                  <div className="flex items-start justify-between">
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-white/5 text-[#8FA4FF]">
                      <m.icon className="h-5 w-5" strokeWidth={1.75} />
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-white/30 transition-colors group-hover:text-white" />
                  </div>
                  <h3 className="mt-6 text-lg font-semibold text-white">
                    {m.title}
                  </h3>
                  <p className="mt-2 text-sm text-white/55">{m.tagline}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="bg-black py-24 md:py-32">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-8">
          <Reveal>
            <Eyebrow>What teams say</Eyebrow>
            <blockquote className="mt-8 text-2xl font-medium leading-snug tracking-tight text-white sm:text-3xl lg:text-4xl">
              “Velacin replaced <span className="text-white/40 line-through">five</span> tools we were
              paying for and three spreadsheets we were pretending to maintain.
              Our managers stopped asking IT for reports — they just open the
              dashboard.”
            </blockquote>
            <div className="mt-8 flex items-center justify-center gap-3">
              <span
                className="grid h-11 w-11 place-items-center rounded-full text-sm font-bold text-white"
                style={{ background: "linear-gradient(135deg, #4C6EF5, #002FA7)" }}
              >
                MK
              </span>
              <div className="text-left">
                <div className="text-sm font-semibold text-white">Maya Krishnan</div>
                <div className="text-xs text-white/50">
                  Head of People, mid-sized product studio
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#0A0A0A] py-24 md:py-32">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <Reveal>
            <div
              className="relative overflow-hidden rounded-[2rem] border border-white/10 p-10 text-center md:p-16"
              style={{
                background:
                  "radial-gradient(120% 120% at 50% 0%, rgba(76,110,245,0.22) 0%, rgba(0,47,167,0.08) 40%, rgba(10,10,10,1) 75%)",
              }}
            >
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                Ready to see it live?
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-base text-white/65 md:text-lg">
                A 20-minute demo tailored to your team — attendance, kanban,
                timesheets, budgets, whatever you're solving for.
              </p>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                <PrimaryButton to="/contact" testId="final-book-demo">
                  Book a demo
                </PrimaryButton>
                <SecondaryButton to="/features" testId="final-see-modules">
                  See every module
                </SecondaryButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
